/**
 * „So arbeitest du mit 99takes in Claude": fuenf Anwendungsfaelle, je einer als
 * kurze Szene in einem gezeichneten Chatfenster. Der Prompt wird sichtbar ins
 * hervorgehobene Eingabefeld getippt und springt beim Senden nach oben, die
 * Werkzeugaufrufe des 99takes-MCP erscheinen nacheinander, dann ein
 * Ergebnis-Ausschnitt.
 *
 * GEZEICHNET, KEIN BILDSCHIRMFOTO, und das steht dabei. Kein Claude- oder
 * Anthropic-Logo, keine claude.ai-Adresse in der Fensterleiste: ein Foto oder
 * Nachbau der fremden Oberflaeche veraltet mit jeder Aenderung dort und liesse
 * sich fuer echt halten. Alle Daten sind erfundene Demodaten.
 *
 * DIE WERKZEUGTITEL kommen aus derselben Datei wie `tools/list`
 * (`supabase/functions/_shared/sim/anzeige.ts`). Eine zweite Liste hier
 * wuerde auseinanderlaufen, und die Seite zeigte Namen, die in Claude nie
 * erscheinen. Wer ein Werkzeug umbenennt, sieht es hier im Typfehler.
 *
 * BEWEGUNG: startet erst, wenn die Szene sichtbar ist, pausiert bei
 * verstecktem Tab, laesst sich anhalten und wiederholen. Bei
 * prefers-reduced-motion steht sofort der Endzustand da und nichts wechselt
 * von selbst. Aller Text ist echter Text und immer im Dokument; die
 * Einblendung ist nur Deckkraft, damit Screenreader die ganze Szene lesen.
 */

import { EINRICHTUNG } from '../preise';
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ANZEIGE } from '../../../../supabase/functions/_shared/sim/anzeige.ts';
import { RandEtikett } from '../noten-je-persona';
import { Kreisel, Marke, Zeichen } from '../ui';
import { t } from '../sprache';

type Werkzeug = keyof typeof ANZEIGE;

interface Szene {
  id: string;
  titel: string;
  /** Was im hinteren Fenster steht: die Frage davor, statisch. */
  vorher: string;
  prompt: string;
  werkzeuge: Array<{ name: Werkzeug; ergebnis: string }>;
  kopf: ReactNode;
  inhalt: ReactNode;
}

// ─── Die fuenf Szenen, erfundene Demodaten ──────────────────────────────────
//
// Hoechstens fuenf (Runde 9): wer mehr zeigen will, legt zusammen statt
// anzubauen. Die Wettbewerber heissen „Anbieter A" und „Anbieter B", damit
// niemand die Szene fuer eine echte Messung haelt.

const Zitat = ({ children }: { children: ReactNode }) => (
  <p className="m-0 mt-1 font-serif text-[0.875rem] leading-snug text-text italic">{t('„', '“')}{children}{t('“', '”')}</p>
);

/** Die Szenen in der Sprache der App. Beim Rendern aufrufen, nicht beim Laden des Moduls. */
const szenen = (): Szene[] => [
  {
    id: 'recherche',
    titel: t('Zielgruppe recherchieren', 'Research an audience'),
    vorher: t('Welche Zielgruppen habe ich?', 'Which audiences do I have?'),
    prompt: t('Lass uns Personas für unsere Lieferanten-Software bauen. Der Traffic kommt über Google.',
      "Let's build personas for our supplier software. The traffic comes from Google."),
    werkzeuge: [
      { name: 'start_research', ergebnis: t('enthalten', 'included') },
      { name: 'read_competitors', ergebnis: t('enthalten', 'included') },
      { name: 'find_voices', ergebnis: t('enthalten', 'included') },
      { name: 'save_persona_set', ergebnis: t(`${EINRICHTUNG} Credits`, `${EINRICHTUNG} credits`) },
    ],
    kopf: t(
      <><b className="font-semibold text-tinte">Einkauf Mittelstand</b> · 12 Personas: 10 Kern, 2 erweitert</>,
      <><b className="font-semibold text-tinte">Mid-sized purchasing</b> · 12 personas: 10 core, 2 extended</>,
    ),
    inhalt: (
      <div className="grid gap-2 sm:grid-cols-2">
        {t<ReadonlyArray<readonly [string, string, string, string, boolean]>>([
          ['Leitung Einkauf', '800 MA', 'Ohne Preis lege ich eine Anbieterseite weg.', 'Beleg: Bewertung', false],
          ['IT-Leitung', '1.200 MA', 'Jede angekündigte Woche Einrichtung wurde bei uns ein Quartal.', 'Beleg: Interview', false],
          ['Controlling', '1.800 MA', 'Ich rechne die Laufzeit, nicht den Monatspreis.', 'Beleg: Forum', false],
          ['Werkstudent Einkauf', '90 MA', 'Ich sammle nur Angebote, entscheiden tun andere.', 'Hypothese, ohne Beleg', true],
        ], [
          ['Head of purchasing', '800 staff', 'Without a price I put a vendor page aside.', 'Proof: review', false],
          ['Head of IT', '1,200 staff', 'Every announced week of setup became a quarter for us.', 'Proof: interview', false],
          ['Financial controller', '1,800 staff', 'I calculate the term, not the monthly price.', 'Proof: forum', false],
          ['Purchasing intern', '90 staff', 'I only collect offers, others decide.', 'Hypothesis, no proof', true],
        ]).map(([rolle, groesse, zitat, beleg, rand]) => (
          <div key={rolle} className="rounded-[0.625rem] border border-linie bg-flaeche px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2 text-[0.75rem] text-text-hell">
              <b className="text-[0.8125rem] font-semibold text-tinte">{rolle}{rand && <RandEtikett />}</b>{groesse}
            </div>
            <Zitat>{zitat}</Zitat>
            <div className="mt-1.5 border-t border-linie-zart pt-1.5 text-[0.6875rem] text-text-hell">{beleg}</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'nur-ihr',
    titel: t('Was sagt nur ihr?', 'What only you say'),
    vorher: t('Hier sind die Startseiten von Anbieter A und Anbieter B.', 'Here are the homepages of Vendor A and Vendor B.'),
    prompt: t('Vergleich unsere Startseite mit Anbieter A und B. Welche Aussagen macht nur ihr?',
      'Compare our homepage with Vendor A and B. Which claims do only we make?'),
    werkzeuge: [
      { name: 'compare_claims', ergebnis: t('4 Credits', '4 credits') },
    ],
    kopf: t(
      <><b className="font-semibold text-tinte">Eine von drei Aussagen macht kein Wettbewerber</b> · sie steht aber weiter unten</>,
      <><b className="font-semibold text-tinte">One of three claims no competitor makes</b> · but it sits further down</>,
    ),
    inhalt: (
      <ul className="m-0 list-none divide-y divide-linie-zart rounded-[0.625rem] border border-linie bg-flaeche p-0">
        {t<ReadonlyArray<readonly ['frei' | 'umstritten' | 'besetzt', string, string, string | null]>>([
          ['frei', 'Festpreis je Standort', 'Steht in Abschnitt 5 von 7. Kandidat für den Hero.', null],
          ['umstritten', 'In einer Woche eingerichtet', 'Startklar in wenigen Tagen', 'Anbieter B'],
          ['besetzt', 'Alle Lieferanten im Blick', 'Alle Lieferanten auf einen Blick', 'Anbieter A'],
        ], [
          ['frei', 'Fixed price per site', 'Sits in section 5 of 7. Candidate for the hero.', null],
          ['umstritten', 'Set up in one week', 'Ready to go in a few days', 'Vendor B'],
          ['besetzt', 'All suppliers in view', 'All suppliers at a glance', 'Vendor A'],
        ]).map(([status, aussage, text, quelle]) => (
          <li key={aussage} className="px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <StatusEtikett status={status} />
              <b className="text-[0.8125rem] font-semibold text-tinte">{aussage}</b>
            </div>
            {quelle ? <Zitat>{text}</Zitat> : <p className="m-0 mt-1 text-[0.75rem] font-medium text-marke-tief">{text}</p>}
            {quelle && <div className="mt-0.5 text-[0.6875rem] text-text-hell">{quelle}</div>}
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: 'pruefen',
    titel: t('Text prüfen', 'Test copy'),
    vorher: t('Was kostet ein Run unserer Startseite gegen Einkauf Mittelstand?', 'What does a run of our homepage against Mid-sized purchasing cost?'),
    prompt: t('Prüf die neue Startseite gegen Einkauf Mittelstand. Unser Versprechen: Festpreis je Standort, ohne Einrichtungsprojekt.',
      'Test the new homepage against Mid-sized purchasing. Our promise: fixed price per site, no setup project.'),
    werkzeuge: [
      { name: 'plan_simulation', ergebnis: t('72 Credits', '72 credits') },
      { name: 'simulate_copy', ergebnis: t('fertig', 'done') },
    ],
    kopf: t(
      <><b className="font-semibold text-tinte">Neue Fassung gewinnt im Kern 34 : 6</b> · Das Urteil folgt dem Kern</>,
      <><b className="font-semibold text-tinte">New version wins in the core 34 : 6</b> · The verdict follows the core</>,
    ),
    inhalt: (
      <>
        <p className="m-0 mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.75rem] text-text-hell">
          <span>{t('Erweiterte Zielgruppe 4 : 4, zählt nicht ins Urteil', "Extended audience 4 : 4, doesn't count toward the verdict")}</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1 w-10 overflow-hidden rounded-full bg-linie-zart" aria-hidden><span className="block h-full w-1/2 rounded-full bg-marke" /></span>
            {t('24 von 48 halten das Versprechen', '24 of 48 say it keeps the promise')}
          </span>
        </p>
        <ul className="m-0 list-none divide-y divide-linie-zart rounded-[0.625rem] border border-linie bg-flaeche p-0">
          {t<ReadonlyArray<readonly [string, string, string, boolean]>>([
            ['Leitung Einkauf', '7,9', 'Endlich steht der Preis oben. Das spart mir den Anruf.', false],
            ['Controlling', '7,6', 'Die Mindestlaufzeit fehlt neben dem Preis.', false],
            ['Werkstudent Einkauf', '5,8', 'Für mich ändert der Preis oben nichts.', true],
          ], [
            ['Head of purchasing', '7.9', 'Finally the price is at the top. That saves me the call.', false],
            ['Financial controller', '7.6', 'The minimum term is missing next to the price.', false],
            ['Purchasing intern', '5.8', 'The price at the top changes nothing for me.', true],
          ]).map(([rolle, note, einwand, rand]) => (
            <li key={rolle} className="px-3 py-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <b className="text-[0.8125rem] font-semibold text-tinte">{rolle}{rand && <RandEtikett />}</b>
                <span className="text-[0.75rem] font-semibold text-marke-tief tabular-nums">{note} / 10</span>
              </div>
              <Zitat>{einwand}</Zitat>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    // Seit 28.09.2026 schreibt 99takes selbst (write_challenger). Die Szene
    // zeigt Schreiben und Vorsortieren zusammen, statt eine sechste anzubauen.
    id: 'schreiben',
    titel: t('Fassungen schreiben lassen', 'Have versions written'),
    vorher: t('Welche Einwände hatte der Run unserer Startseite?', 'Which objections did the run of our homepage have?'),
    prompt: t('Schreib aus den Einwänden des letzten Runs drei bessere Fassungen unserer Hero und sortier sie vor.',
      "Write three better versions of our hero from the last run's objections and preselect them."),
    werkzeuge: [
      { name: 'write_challenger', ergebnis: t('6 Credits', '6 credits') },
      { name: 'preselect_copy', ergebnis: t('4 Credits', '4 credits') },
    ],
    kopf: t(
      <><b className="font-semibold text-tinte">3 Fassungen, vorsortiert</b> · keine Messung, der Sieger geht in den vollen Run</>,
      <><b className="font-semibold text-tinte">3 versions, preselected</b> · not a measurement, the winner goes into the full run</>,
    ),
    inhalt: (
      <ol className="m-0 list-none divide-y divide-linie-zart rounded-[0.625rem] border border-linie bg-flaeche p-0">
        {t<ReadonlyArray<readonly [string, string, string]>>([
          ['Bisher, ab jetzt', 'Standorte kommen dazu, der Preis nicht. Festpreis je Standort, ohne Einrichtungsprojekt.', '7,4'],
          ['Beleg zuerst', 'Festpreis je Standort, Laufzeit [BELEG: Mindestlaufzeit]. Ohne Einrichtungsprojekt.', '6,9'],
          ['Nutzengetrieben', 'Der nächste Standort kostet, was der erste gekostet hat.', '6,1'],
        ], [
          ['Before, from now on', "Sites get added, the price doesn't. Fixed price per site, no setup project.", '7.4'],
          ['Proof first', 'Fixed price per site, term [BELEG: minimum term]. No setup project.', '6.9'],
          ['Benefit-driven', 'The next site costs what the first one cost.', '6.1'],
        ]).map(([methode, text, note], i) => (
          <li key={methode} className="grid grid-cols-[1.25rem_minmax(0,1fr)_2.25rem] gap-2 px-3 py-2.5">
            <span className="font-serif text-[1rem] leading-none font-semibold text-marke-tief tabular-nums">{i + 1}</span>
            <span className="min-w-0">
              <Marke>{methode}</Marke>
              <Zitat>{text}</Zitat>
            </span>
            <span className="text-right text-[0.75rem] text-text-hell tabular-nums">{note}</span>
          </li>
        ))}
      </ol>
    ),
  },
  {
    id: 'bericht',
    titel: t('Bericht teilen', 'Share a report'),
    vorher: t('Fass die Studie Startseite Q4 für die Geschäftsführung zusammen.', 'Summarize the study Homepage Q4 for management.'),
    prompt: t('Mach aus der Studie Startseite Q4 einen Bericht mit Link, den ich weiterleiten kann.',
      'Turn the study Homepage Q4 into a report with a link I can forward.'),
    werkzeuge: [
      { name: 'publish_report', ergebnis: t('Link erstellt', 'Link created') },
    ],
    kopf: t(
      <><b className="font-semibold text-tinte">Bericht veröffentlicht</b> · eingefroren, Link zurückziehbar</>,
      <><b className="font-semibold text-tinte">Report published</b> · frozen, link can be withdrawn</>,
    ),
    inhalt: (
      <div className="immer-hell rounded-[0.625rem] border border-linie bg-[#f4f5f2] p-3.5">
        <div className="font-mono text-[0.625rem] font-bold tracking-[0.18em] text-marke-tief uppercase">{t('Zielgruppen-Simulation', 'Audience simulation')}</div>
        <div className="mt-1 font-serif text-[1.25rem] leading-tight font-semibold text-tinte">{t('Startseite Q4', 'Homepage Q4')}</div>
        <div className="mt-1 text-[0.75rem] text-text-hell">{t('3 Fassungen · 12 Personas · Stand heute', '3 versions · 12 personas · as of today')}</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 truncate rounded-md border border-linie bg-flaeche px-2.5 py-1.5 font-mono text-[0.6875rem] text-text-leise">
            read.99takes.com/report?t=beispiel
          </span>
          <span className="rounded-md border border-linie bg-flaeche px-2.5 py-1.5 text-[0.75rem] font-medium text-text">{t('Link kopieren', 'Copy link')}</span>
        </div>
      </div>
    ),
  },
];

/** Status aus compare_claims wie im Bericht: frei gruen, besetzt als Warnung. */
function StatusEtikett({ status }: { status: 'frei' | 'umstritten' | 'besetzt' }) {
  return status === 'frei' ? <Marke art="gut">{t('frei', 'free')}</Marke> : status === 'besetzt' ? <Marke art="schlecht">{t('besetzt', 'taken')}</Marke> : <Marke>{t('umstritten', 'contested')}</Marke>;
}

// ─── Zeitplan einer Szene ───────────────────────────────────────────────────
//
// Alles haengt an einer vergangenen Zeit in Millisekunden. Anhalten heisst
// dann nur: die Uhr steht. Kein Geflecht aus Timern, das man beim Pausieren
// wieder einsammeln muesste.

// Seit 28.09.2026 (Feedback Testnutzer: das Beispiel war erst beim zweiten
// Hinsehen verstaendlich) steht das Tippen im Vordergrund: erst leuchtet das
// Eingabefeld mit „Du tippst in Claude" auf, dann erscheint die Frage dort
// Zeichen fuer Zeichen, und beim Senden springt sie als Blase nach oben.

/** Das Feld steht schon hervorgehoben da, bevor das erste Zeichen kommt. */
const VORLAUF_MS = 700;
const TIPPEN_MS = 24;
/** Kurz stehen lassen, was getippt ist, bevor es abgeschickt wird. */
const SENDEN_MS = 450;
const WERKZEUG_MS = 900;
const HALTEN_MS = 5000;

export function zeitplan(s: Pick<Szene, 'prompt' | 'werkzeuge'>) {
  const tippenAb = VORLAUF_MS;
  const getippt = tippenAb + s.prompt.length * TIPPEN_MS;
  const gesendet = getippt + SENDEN_MS;
  const werkzeug = (i: number) => gesendet + 450 + i * WERKZEUG_MS;
  const ergebnis = werkzeug(s.werkzeuge.length) - 250;
  return { tippenAb, getippt, gesendet, werkzeug, ergebnis, ende: ergebnis + HALTEN_MS };
}

/**
 * Was das Eingabefeld zur Zeit `zeit` zeigt. `Infinity` ist der Endzustand
 * (prefers-reduced-motion): abgeschickt, das Feld leer und nicht hervorgehoben.
 */
export function eingabe(s: Pick<Szene, 'prompt' | 'werkzeuge'>, zeit: number) {
  const plan = zeitplan(s);
  const gesendet = zeit >= plan.gesendet;
  const zeichen = gesendet ? s.prompt.length : Math.max(0, Math.min(s.prompt.length, Math.floor((zeit - plan.tippenAb) / TIPPEN_MS)));
  return { gesendet, tippt: !gesendet, zeichen };
}

const bewegungAus = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function SoSiehtEsAus({ ohneKopf = false, aktionen }: {
  /** In der Anleitung traegt der Seitenkopf Titel und Einleitung; dann steht hier keine zweite Ueberschrift. */
  ohneKopf?: boolean;
  /**
   * Ersetzt die Knoepfe unter den Reitern. Im Rundgang nach der Registrierung
   * gibt es „Alle Ablaeufe" nicht (der Anker liegt auf /guide), und der erste
   * Knopf fuehrt weiter statt ins Konto.
   */
  aktionen?: ReactNode;
} = {}) {
  const [aktiv, setAktiv] = useState(0);
  const [zeit, setZeit] = useState(() => (bewegungAus() ? Infinity : 0));
  const [laeuft, setLaeuft] = useState(() => !bewegungAus());
  const [automatisch, setAutomatisch] = useState(() => !bewegungAus());
  const [sichtbar, setSichtbar] = useState(false);
  const [tabSichtbar, setTabSichtbar] = useState(() => typeof document === 'undefined' || !document.hidden);
  const buehne = useRef<HTMLDivElement>(null);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const basis = useId();

  const SZENEN = szenen();
  const szene = SZENEN[aktiv]!;
  const plan = zeitplan(szene);

  // Erst starten, wenn die Szene zu sehen ist.
  useEffect(() => {
    const el = buehne.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSichtbar(true); return; }
    const io = new IntersectionObserver(([e]) => setSichtbar(!!e?.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Versteckter Tab: die Uhr steht.
  useEffect(() => {
    const f = () => setTabSichtbar(!document.hidden);
    document.addEventListener('visibilitychange', f);
    return () => document.removeEventListener('visibilitychange', f);
  }, []);

  useEffect(() => {
    if (!laeuft || !sichtbar || !tabSichtbar || zeit === Infinity) return;
    const t = setInterval(() => setZeit(z => z + 50), 50);
    return () => clearInterval(t);
  }, [laeuft, sichtbar, tabSichtbar, zeit === Infinity]);

  // Am Ende der Szene zur naechsten, solange niemand selbst gewaehlt hat.
  useEffect(() => {
    if (zeit < plan.ende || zeit === Infinity) return;
    if (automatisch) { setAktiv(a => (a + 1) % SZENEN.length); setZeit(0); }
    else setLaeuft(false);
  }, [zeit, plan.ende, automatisch]);

  const waehle = useCallback((i: number) => {
    setAktiv(i);
    setAutomatisch(false);
    if (bewegungAus()) { setZeit(Infinity); return; }
    setZeit(0);
    setLaeuft(true);
  }, []);

  const nochmal = () => {
    if (bewegungAus()) return;
    setZeit(0);
    setLaeuft(true);
  };

  function tastatur(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    const ziel = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? (i + 1) % SZENEN.length
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? (i - 1 + SZENEN.length) % SZENEN.length
        : e.key === 'Home' ? 0 : e.key === 'End' ? SZENEN.length - 1 : null;
    if (ziel == null) return;
    e.preventDefault();
    waehle(ziel);
    tabs.current[ziel]?.focus();
  }

  const { zeichen, gesendet, tippt } = eingabe(szene, zeit);
  const ergebnisDa = zeit >= plan.ergebnis;
  const endzustand = zeit === Infinity;

  return (
    <section
      aria-labelledby={ohneKopf ? undefined : `${basis}-titel`}
      aria-label={ohneKopf ? t('Beispiele in Claude', 'Examples in Claude') : undefined}
      className="grid items-center gap-x-12 gap-y-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
    >
      {/* Die Aufklappliste „Alle Werkzeuge" steht unten in einer eigenen
          Grid-Zeile (siehe Ende). In der rechten Spalte machte sie die erste
          Zeile hoeher, und die Reiter links rutschten beim Aufklappen mit der
          Mitte nach unten (Feedback Testnutzer, 28.09.2026). */}
      <div className="min-w-0">
        {!ohneKopf && (
          <>
            <h2 id={`${basis}-titel`} className="m-0 font-serif text-[1.625rem] leading-[1.12] font-semibold tracking-[-0.015em] text-tinte sm:text-[2rem]">
              {t('So arbeitest du mit 99takes in Claude', 'How you work with 99takes in Claude')}
            </h2>
            <p className="mt-3 mb-6 max-w-[46ch] text-[0.9375rem] leading-relaxed text-text-leise">
              {t(<>
                Du fragst in Claude, 99takes prüft gegen recherchierte Personas deiner Zielgruppe, ob Firmen, Privatkunden oder Wähler. Bevor etwas Credits kostet,
                nennt Claude den Preis. Runs mit gleicher Eingabe kosten nichts.
              </>, <>
                You ask in Claude, and 99takes tests against researched personas from your audience, whether businesses, consumers or voters. Before anything
                costs credits, Claude names the price. Runs with the same input cost nothing.
              </>)}
            </p>
          </>
        )}

        {/* Aktiver Reiter als Flaeche statt Seitenstreifen: ein farbiger
            Rand links ist ein Muster, das hier nichts traegt ausser Deko. */}
        <div role="tablist" aria-orientation="vertical" aria-label={t('Anwendungsfälle', 'Use cases')} className="flex flex-col gap-0.5">
          {SZENEN.map((s, i) => (
            <button
              key={s.id}
              ref={el => { tabs.current[i] = el; }}
              role="tab" type="button"
              id={`${basis}-tab-${s.id}`}
              aria-selected={i === aktiv}
              aria-controls={`${basis}-szene`}
              tabIndex={i === aktiv ? 0 : -1}
              onClick={() => waehle(i)}
              onKeyDown={e => tastatur(e, i)}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left text-[0.96875rem] font-medium transition-[background-color,border-color,color] duration-200 focus-visible:outline-offset-[-2px] ${
                i === aktiv ? 'border-linie bg-flaeche text-tinte shadow-flaeche' : 'border-transparent bg-transparent text-text-hell hover:bg-flaeche/60 hover:text-text-leise'}`}
            >
              <span className={`font-mono text-[0.75rem] font-semibold ${i === aktiv ? 'text-marke-tief' : 'text-text-hell'}`}>{String(i + 1).padStart(2, '0')}</span>
              {s.titel}
            </button>
          ))}
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          {aktionen ?? <>
          <Link
            to="/account#verbinden"
            className="inline-flex h-[2.625rem] items-center rounded-lg bg-knopf px-[1.125rem] text-[0.90625rem] font-semibold text-auf-marke no-underline shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(20_18_12/0.18)] transition-colors duration-150 hover:bg-marke-tief"
          >
            {t('Mit Claude verbinden', 'Connect to Claude')}
          </Link>
          <a href="#ablaeufe" className="text-[0.90625rem] font-medium text-marke-tief no-underline hover:underline hover:underline-offset-[3px]">
            {t('Alle Abläufe', 'All workflows')} <span aria-hidden>→</span>
          </a>
          </>}
        </div>
      </div>

      <div className="min-w-0">
        <div ref={buehne} className="relative sm:pt-7 sm:pl-7">
          {/* Das hintere Fenster: die Frage davor. Auf dem Telefon weg. */}
          <div aria-hidden className="absolute top-0 right-0 hidden w-[76%] rounded-[1.125rem] bg-geraet p-1.5 shadow-[0_18px_40px_-18px_rgb(20_18_12/0.45)] sm:block">
            <div className="h-[17rem] overflow-hidden rounded-[0.8125rem] bg-flaeche">
              <Fensterleiste />
              <div className="p-4">
                <div className="ml-auto max-w-[80%] rounded-xl bg-blase px-3 py-2 text-[0.8125rem] leading-snug text-text">{szene.vorher}</div>
              </div>
            </div>
          </div>

          <div className="relative z-10 rounded-[1.125rem] bg-geraet p-1.5 shadow-[0_24px_48px_-20px_rgb(20_18_12/0.5)] sm:mt-8 sm:w-[90%]">
            <div
              id={`${basis}-szene`} role="tabpanel" aria-labelledby={`${basis}-tab-${szene.id}`}
              className="overflow-hidden rounded-[0.8125rem] bg-flaeche"
            >
              <Fensterleiste />
              <div className="flex min-h-[23rem] flex-col p-3.5 sm:p-4">
                {/* Die Frage: sichtbar Zeichen fuer Zeichen unten im Feld, vorgelesen
                    einmal am Stueck hier. Beim Senden springt sie von unten herauf. */}
                <div className={`ml-auto max-w-[86%] rounded-xl bg-blase px-3 py-2 text-[0.84375rem] leading-snug text-text transition-[opacity,translate] duration-300 ease-out motion-reduce:transition-none ${
                  gesendet ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
                  <span className="sr-only">{t('Du fragst: ', 'You ask: ')}</span>{szene.prompt}
                </div>

                <ul className="m-0 mt-3 list-none space-y-1.5 p-0">
                  {szene.werkzeuge.map((w, i) => {
                    const da = zeit >= plan.werkzeug(i);
                    const fertig = zeit >= plan.werkzeug(i) + 600;
                    return (
                      <li
                        key={w.name}
                        className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[0.78125rem] text-text-leise transition-[opacity,translate] duration-300 ${
                          da ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'}`}
                      >
                        <Zeichen klein className="size-4" />
                        <b className="font-semibold text-tinte">{ANZEIGE[w.name].title}</b>
                        <code className="font-mono text-[0.71875rem] text-text-hell">{w.name}</code>
                        <span className="ml-auto inline-flex items-center gap-1 text-[0.75rem] text-text-hell">
                          {fertig || endzustand
                            ? <span className={/credit/i.test(w.ergebnis) ? 'font-medium text-marke-tief' : 'text-gut'}>{w.ergebnis}</span>
                            : <Kreisel className="size-3" />}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className={`mt-3 transition-[opacity,translate] duration-500 ${ergebnisDa ? 'translate-y-0 opacity-100' : 'translate-y-1.5 opacity-0'}`}>
                  <div className="mb-2 text-[0.8125rem] leading-snug text-text-leise">{szene.kopf}</div>
                  {szene.inhalt}
                </div>

                {/* Eingabefeld: hier wird getippt, hervorgehoben, solange getippt
                    wird; danach leer. aria-hidden, die Frage steht oben einmal. */}
                <div aria-hidden className="mt-auto pt-4">
                  <div className={`mb-1.5 text-[0.6875rem] font-semibold tracking-[0.06em] text-marke-tief uppercase transition-opacity duration-200 ${tippt ? 'opacity-100' : 'opacity-0'}`}>
                    {t('Du tippst in Claude', 'You type in Claude')}
                  </div>
                  <div className={`flex items-end gap-2.5 rounded-xl border px-3 py-2 transition-[background-color,border-color,box-shadow] duration-200 ${
                    tippt ? 'border-marke bg-marke/[0.07] shadow-[0_0_0_3px_rgb(63_138_135/0.14)]' : 'border-linie bg-flaeche'}`}>
                    {/* Die ganze Frage unsichtbar darunter haelt die Hoehe: das
                        Fenster springt weder beim Tippen noch beim Senden. */}
                    <span className="grid min-w-0 flex-1 text-[0.8125rem] leading-snug">
                      <span className="invisible col-start-1 row-start-1">{szene.prompt}</span>
                      <span className="col-start-1 row-start-1 text-text">
                        {tippt
                          ? <>{szene.prompt.slice(0, zeichen)}<span className="ml-px inline-block h-[1em] w-px translate-y-[2px] bg-text motion-safe:animate-pulse" /></>
                          : <span className="text-text-hell">{t('Antworten …', 'Reply …')}</span>}
                      </span>
                    </span>
                    <span className={`grid size-7 shrink-0 place-items-center rounded-lg text-auf-marke transition-colors duration-200 ${tippt && zeichen > 0 ? 'bg-knopf' : 'bg-linie-stark'}`}>↑</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:pl-7">
          <p className="m-0 min-w-0 flex-1 basis-56 text-[0.75rem] leading-relaxed text-text-hell">
            {t('Beispiel mit erfundenen Daten. Aussehen und Wortlaut von Claude weichen ab.', "Example with made-up data. Claude's look and wording differ.")}
          </p>
          {!endzustand && (
            <span className="flex gap-2">
              <button
                type="button" onClick={() => { setLaeuft(l => !l); setAutomatisch(false); }}
                className="cursor-pointer rounded-md border border-linie bg-flaeche px-2.5 py-1 text-[0.75rem] text-text-leise hover:text-tinte"
              >
                {laeuft ? t('Anhalten', 'Pause') : t('Weiter abspielen', 'Resume')}
              </button>
              <button
                type="button" onClick={nochmal}
                className="cursor-pointer rounded-md border border-linie bg-flaeche px-2.5 py-1 text-[0.75rem] text-text-leise hover:text-tinte"
              >
                {t('Nochmal', 'Again')}
              </button>
            </span>
          )}
        </div>
      </div>

      <details className="-mt-5 min-w-0 sm:pl-7 lg:col-start-2">
        <summary className="cursor-pointer text-[0.8125rem] font-medium text-text-leise hover:text-tinte">{t('Alle Werkzeuge, wie Claude sie anzeigt', 'All tools, as Claude shows them')}</summary>
        <AlleWerkzeuge />
      </details>
    </section>
  );
}

function Fensterleiste() {
  return (
    <div className="flex items-center justify-between border-b border-linie-zart px-3.5 py-2 text-[0.6875rem] text-text-hell">
      <span aria-hidden className="flex gap-1.5">
        <i className="size-[7px] rounded-full bg-linie-stark" /><i className="size-[7px] rounded-full bg-linie-stark" /><i className="size-[7px] rounded-full bg-linie-stark" />
      </span>
      <span>{t('In Claude · Beispiel', 'In Claude · example')}</span>
      <span className="w-[29px]" />
    </div>
  );
}

const lesend = Object.values(ANZEIGE).filter(a => a.readOnly).map(a => a.title);
const schreibend = Object.values(ANZEIGE).filter(a => !a.readOnly).map(a => a.title);

function AlleWerkzeuge() {
  return (
    <div className="mt-3">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1.5 text-[0.8125rem] font-semibold text-tinte">{t('Liest nur', 'Read only')} · {lesend.length}</div>
          <ul className="m-0 list-none p-0 text-[0.8125rem] leading-relaxed text-text-leise">{lesend.map(titel => <li key={titel}>{titel}</li>)}</ul>
        </div>
        <div>
          <div className="mb-1.5 text-[0.8125rem] font-semibold text-tinte">{t('Schreibt oder kostet Credits', 'Writes or costs credits')} · {schreibend.length}</div>
          <ul className="m-0 list-none p-0 text-[0.8125rem] leading-relaxed text-text-leise">{schreibend.map(titel => <li key={titel}>{titel}</li>)}</ul>
        </div>
      </div>
      <p className="mt-3 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
        {t(<>
          Lesendes kannst du in Claude dauerhaft erlauben. Was Credits kostet, trägt das im Namen.
          Das lässt du dir besser jedes Mal zeigen.
        </>, <>
          You can allow read-only tools in Claude permanently. Tools that cost credits say so in their name.
          Better have those shown to you each time.
        </>)}
      </p>
    </div>
  );
}
